<!-- ─── FAB BUTTON ──────────────────────────────────────────────────────────── -->
<div class="chatbot-fab" (click)="togglePanel()" [class.fab-open]="isOpen" title="CMP GENIE">
  <span class="fab-icon" *ngIf="!isOpen">💬</span>
  <span class="fab-icon" *ngIf="isOpen">✕</span>
  <span class="fab-pulse" *ngIf="!isOpen"></span>
</div>

<!-- ─── CHAT PANEL ─────────────────────────────────────────────────────────── -->
<div class="chatbot-panel" [class.panel-open]="isOpen" [class.fullscreen]="isFullscreen">

  <!-- HEADER -->
  <div class="chatbot-header">
    <div class="header-left">
      <div class="bot-avatar">🤖</div>
      <div class="header-info">
        <span class="header-title">CMP AI ASSISTANT</span>
        <span class="header-subtitle">Data Intelligence Assistant</span>
      </div>
    </div>
    <div class="header-actions">
      <button class="icon-btn" (click)="toggleFullscreen()" [title]="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'">
        {{ isFullscreen ? '⛶' : '⛶' }}
      </button>
      <button class="icon-btn" (click)="clearConversation()" title="Clear conversation">🗑️</button>
      <button class="icon-btn" (click)="exportChat()" title="Export chat to CSV">⬇️</button>
      <button class="icon-btn" (click)="closePanel()" title="Close">✕</button>
    </div>
  </div>

  <!-- MESSAGES -->
  <div class="messages-container" #messagesContainer>
    <ng-container *ngFor="let msg of messages; trackBy: trackByIndex">

      <!-- ── USER MESSAGE ── -->
      <div class="message-row user-row" *ngIf="msg.role === 'user'">
        <div class="message-bubble user-bubble">
          <span class="message-text">{{ msg.content }}</span>
          <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
        </div>
        <div class="user-avatar">👤</div>
      </div>

      <!-- ── ASSISTANT MESSAGE ── -->
      <div class="message-row bot-row" *ngIf="msg.role === 'assistant'">
        <div class="bot-avatar-small">🤖</div>
        <div class="bot-message-content">

          <!-- SIMPLE LOADING DOTS ONLY -->
          <div class="message-bubble bot-bubble loading-bubble" *ngIf="msg.isLoading">
            <div class="loading-dots">
              <span></span><span></span><span></span>
            </div>
          </div>

          <!-- ACTUAL MESSAGE -->
          <div class="message-bubble bot-bubble" *ngIf="!msg.isLoading">

            <!-- TOOLS USED PILLS -->
            <div class="tools-used" *ngIf="msg.toolsUsed && msg.toolsUsed.length">
              <span class="tool-pill" *ngFor="let tool of msg.toolsUsed">
                🔧 {{ tool }}
              </span>
            </div>

            <!-- MESSAGE TEXT / TABLES -->
            <div class="message-rich-content">
              <ng-container *ngFor="let part of getMessageParts(msg); let i = index; trackBy: trackByIndex">
                <div class="message-text" *ngIf="part.type === 'text'">{{ part.text }}</div>

                <div class="table-wrapper" *ngIf="part.type === 'table'">
                  <table class="chat-table">
                    <thead>
                      <tr>
                        <th *ngFor="let header of part.headers">{{ header }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of part.rows">
                        <td *ngFor="let cell of row">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ng-container>
            </div>

            <app-chatbot-visualization
              *ngIf="msg.visualization?.enabled"
              [visualization]="msg.visualization">
            </app-chatbot-visualization>

            <!-- WARNINGS -->
            <div class="warnings-section" *ngIf="msg.warnings && msg.warnings.length">
              <div
                class="warning-badge"
                *ngFor="let w of msg.warnings"
                [ngClass]="getWarningClass(w)">
                {{ getWarningIcon(w) }} {{ w }}
              </div>
            </div>

            <!-- INSIGHTS -->
            <div class="insights-section" *ngIf="msg.insights && msg.insights.length">
              <div class="insights-header">💡 Key Insights</div>
              <ul class="insights-list">
                <li *ngFor="let insight of msg.insights">{{ insight }}</li>
              </ul>
            </div>

            <span class="message-time">{{ formatTime(msg.timestamp) }}</span>

          </div>

          <!-- FOLLOW-UP SUGGESTIONS -->
          <div
            class="suggestions-row"
            *ngIf="!msg.isLoading && msg.followUpSuggestions && msg.followUpSuggestions.length">
            <button
              class="suggestion-pill"
              *ngFor="let s of msg.followUpSuggestions"
              (click)="onSuggestionClick(s)">
              {{ s }}
            </button>
          </div>

        </div>
      </div>

    </ng-container>

    <!-- ═══ QUICK ACTIONS (SHOW AFTER WELCOME MESSAGE) ═══ -->
    <div class="quick-actions-section" *ngIf="quickActions && quickActions.length && messages.length === 1">
      <div class="quick-actions-grid">
        <button 
          class="quick-action-btn" 
          *ngFor="let action of quickActions"
          (click)="sendMessage(action.query)">
          {{ action.label }}
        </button>
      </div>
    </div>
    <!-- ═══ END QUICK ACTIONS ═══ -->
    
    <!-- ═══ PREDEFINED QUESTIONS SECTION (AFTER MESSAGES) ═══ -->
    <div class="predefined-section" *ngIf="showPredefinedQuestions && messages.length > 0">
      <div class="predefined-header" (click)="togglePredefinedQuestions()">
        <span class="predefined-title">
          <span class="icon">💡</span>
          More Questions - {{ isPredefinedExpanded ? 'Click to collapse' : 'Click to expand' }}
        </span>
        <span class="toggle-icon">{{ isPredefinedExpanded ? '▼' : '▶' }}</span>
      </div>

      <div class="predefined-content" *ngIf="isPredefinedExpanded">
        <div class="questions-list">
          <button 
            class="predefined-question-btn" 
            *ngFor="let q of predefinedQuestions; let i = index"
            (click)="selectPredefinedQuestion(q)"
            [attr.data-level]="i < 4 ? 'easy' : (i < 8 ? 'intermediate' : (i < 12 ? 'advanced' : 'expert'))">
            <span class="q-text">{{ q }}</span>
          </button>
        </div>
      </div>
    </div>
    <!-- ═══ END PREDEFINED QUESTIONS ═══ -->
  </div>

  <!-- INPUT AREA -->
  <div class="input-area">
    <textarea
      #queryInput
      class="query-input"
      [(ngModel)]="userQuery"
      (keydown)="onKeyDown($event)"
      placeholder="Ask me about FE capacities, water data, gas plants..."
      rows="1"
      [disabled]="isLoading">
    </textarea>
    <button
      class="send-btn"
      (click)="sendMessage()"
      [disabled]="!userQuery.trim() || isLoading"
      title="Send message">
      <span *ngIf="!isLoading">➤</span>
      <span *ngIf="isLoading" class="sending-spinner"></span>
    </button>
  </div>




  <!-- FOOTER NOTE -->
  <div class="chatbot-footer">
    <span>Powered by Azure OpenAI · Data from SMARTER Blob Storage</span>
  </div>

</div>
